import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Completion,
  HistoryRecord,
  SavedGame,
  TutorialProgress,
  ConsentStatus,
  TrainSettings,
} from './types';
import { createTrainSeed, normalizeTrainSeedInput } from './trainSeed';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

interface GameState {
  completions: Record<string, Completion>;
  history: Record<string, HistoryRecord>;
  savedGames: Record<string, SavedGame>;
  activeSaveByCategory: Record<string, string>;
  tutorialProgress: TutorialProgress;
  consent: ConsentStatus | null;
  soundEnabled: boolean;
  musicEnabled: boolean;
  effectsEnabled: boolean;
  cellStateCycleEnabled: boolean;
  autoPauseEnabled: boolean;
  spanHintsEnabled: boolean;
  timerVisible: boolean;
  trainSettings: TrainSettings;

  // Actions
  savePuzzleCompletion: (key: string, time: number, hints?: number) => void;
  recordHistory: (record: HistoryRecord) => void;
  saveGameState: (key: string, grid: string[][], elapsed: number) => void;
  loadGameState: (key: string) => SavedGame | null;
  clearGameState: (key: string) => void;
  clearCategorySave: (key: string) => void;
  saveTutorialLessonComplete: (index: number) => void;
  saveConsent: (status: ConsentStatus) => void;
  toggleSound: () => boolean;
  toggleMusic: () => boolean;
  toggleCellStateCycle: () => boolean;
  toggleAutoPause: () => boolean;
  toggleSpanHints: () => boolean;
  toggleTimerVisible: () => boolean;
  saveTrainSettings: (settings: TrainSettings) => void;
}

// ---------------------------------------------------------------------------
// Derived helpers (pure functions, not part of the store)
// ---------------------------------------------------------------------------

export function getBestTime(
  completions: Record<string, Completion>,
  key: string
): number | null {
  return completions[key]?.time ?? null;
}

export function isLevelUnlocked(
  completions: Record<string, Completion>,
  index: number,
  prevLevelId?: string
): boolean {
  if (index === 0) return true;
  // Use previous level's ID-based key if available, fall back to index-based
  if (prevLevelId) return !!completions[prevLevelId];
  return !!completions[`level_${index - 1}`];
}

export function isSubPuzzleComplete(
  completions: Record<string, Completion>,
  levelId: string,
  puzzleId: string
): boolean {
  return !!completions[`${levelId}:${puzzleId}`];
}

export function isLevelComplete(
  completions: Record<string, Completion>,
  levelId: string
): boolean {
  return !!completions[levelId];
}

/** Read all game settings from the store — spread as props to Game. */
export function useGameSettings() {
  const trainSettings = useGameStore((s) => s.trainSettings);
  const timerVisible = useGameStore((s) => s.timerVisible);
  const cellStateCycleEnabled = useGameStore((s) => s.cellStateCycleEnabled);
  const spanHintsEnabled = useGameStore((s) => s.spanHintsEnabled);

  return {
    // Level rules (from trainSettings — locked during play)
    strict: trainSettings.strict,
    hintCount: trainSettings.hintCount,
    autoMark: trainSettings.autoMark,
    validateCount: trainSettings.validateCount,
    // Preferences (global — always changeable)
    timerVisible,
    cellStateCycleEnabled,
    spanHintsEnabled,
  };
}

export function isTutorialLessonUnlocked(
  progress: TutorialProgress,
  index: number
): boolean {
  if (index === 0) return true;
  return !!progress.lessonsComplete[index - 1];
}

function getSaveCategory(key: string): string {
  if (key.startsWith('tutorial_')) return 'tutorial';
  if (key.startsWith('shared_')) return 'shared';
  if (key.startsWith('train_')) return 'train';
  return 'levels';
}

function deriveActiveSaves(
  savedGames: Record<string, SavedGame>
): Record<string, string> {
  const grouped = new Map<string, string[]>();

  for (const key of Object.keys(savedGames)) {
    const category = getSaveCategory(key);
    const keys = grouped.get(category) ?? [];
    keys.push(key);
    grouped.set(category, keys);
  }

  const next: Record<string, string> = {};

  for (const [category, keys] of grouped) {
    if (keys.length === 1) {
      next[category] = keys[0];
      continue;
    }

    const withUpdatedAt = keys
      .map((key) => ({ key, updatedAt: savedGames[key]?.updatedAt ?? 0 }))
      .filter((entry) => entry.updatedAt > 0)
      .sort((a, b) => b.updatedAt - a.updatedAt);

    if (withUpdatedAt.length > 0) {
      next[category] = withUpdatedAt[0].key;
    }
  }

  return next;
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

const DEFAULT_TUTORIAL: TutorialProgress = {
  lessonsComplete: [],
  puzzlesComplete: [],
};

function createDefaultTrainSettings(): TrainSettings {
  return {
    size: 5,
    density: 'normal',
    seed: createTrainSeed(),
    strict: false,
    hintCount: 3, // default 3 hints
    autoMark: false,
    validateCount: -1, // unlimited by default
    difficulty: 'casual',
  };
}

function normalizeTrainSettings(
  settings?:
    | (Partial<TrainSettings> & {
        playMode?: 'normal' | 'free' | 'custom';
      })
    | null
): TrainSettings {
  const defaults = createDefaultTrainSettings();
  const { playMode, ...restSettings } = settings ?? {};
  const seedSource =
    typeof restSettings.seed === 'string'
      ? restSettings.seed
      : typeof restSettings.seed === 'number'
      ? String(restSettings.seed)
      : defaults.seed;

  const normalizedSettings: TrainSettings = {
    ...defaults,
    ...restSettings,
    seed: normalizeTrainSeedInput(seedSource) || defaults.seed,
  };

  if (playMode === 'normal') {
    return {
      ...normalizedSettings,
      strict: true,
      hintCount: 3,
      autoMark: false,
    };
  }

  if (playMode === 'free') {
    return {
      ...normalizedSettings,
      strict: false,
      hintCount: -1, // unlimited in free mode
      autoMark: false,
    };
  }

  return {
    ...normalizedSettings,
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      completions: {},
      history: {},
      savedGames: {},
      activeSaveByCategory: {},
      tutorialProgress: DEFAULT_TUTORIAL,
      consent: null,
      soundEnabled: true,
      musicEnabled: true,
      effectsEnabled: true,
      cellStateCycleEnabled: false,
      autoPauseEnabled: true,
      spanHintsEnabled: false,
      timerVisible: true,
      trainSettings: createDefaultTrainSettings(),

      savePuzzleCompletion(key, time, hints = 0) {
        set((s) => {
          const prev = s.completions[key];
          if (prev && time >= prev.time) return s;
          return {
            completions: {
              ...s.completions,
              [key]: { time, completedAt: Date.now(), hints },
            },
          };
        });
      },

      recordHistory(record) {
        set((s) => ({
          history: { ...s.history, [record.id]: record },
        }));
      },

      saveGameState(key, grid, elapsed) {
        set((s) => {
          const category = getSaveCategory(key);
          const savedGames = {
            ...s.savedGames,
            [key]: { grid, elapsed, updatedAt: Date.now() },
          };

          for (const existingKey of Object.keys(savedGames)) {
            if (
              existingKey !== key &&
              getSaveCategory(existingKey) === category
            ) {
              delete savedGames[existingKey];
            }
          }

          return {
            savedGames,
            activeSaveByCategory: {
              ...s.activeSaveByCategory,
              [category]: key,
            },
          };
        });
      },

      loadGameState(key) {
        const category = getSaveCategory(key);
        if (get().activeSaveByCategory[category] !== key) return null;
        return get().savedGames[key] ?? null;
      },

      clearGameState(key) {
        set((s) => {
          const category = getSaveCategory(key);
          const { [key]: _, ...rest } = s.savedGames;
          const activeSaveByCategory = { ...s.activeSaveByCategory };
          if (activeSaveByCategory[category] === key) {
            delete activeSaveByCategory[category];
          }
          return { savedGames: rest, activeSaveByCategory };
        });
      },

      clearCategorySave(key) {
        set((s) => {
          const category = getSaveCategory(key);
          const activeKey = s.activeSaveByCategory[category];
          if (!activeKey) return s;

          const { [activeKey]: _, ...savedGames } = s.savedGames;
          const activeSaveByCategory = { ...s.activeSaveByCategory };
          delete activeSaveByCategory[category];

          return { savedGames, activeSaveByCategory };
        });
      },

      saveTutorialLessonComplete(index) {
        set((s) => {
          const lc = [...s.tutorialProgress.lessonsComplete];
          while (lc.length <= index) lc.push(false);
          lc[index] = true;
          return {
            tutorialProgress: { ...s.tutorialProgress, lessonsComplete: lc },
          };
        });
      },

      saveConsent(status) {
        set({ consent: status });
      },

      toggleSound() {
        const next = !get().soundEnabled;
        set({ soundEnabled: next });
        return next;
      },

      toggleMusic() {
        const next = !get().musicEnabled;
        set({ musicEnabled: next });
        return next;
      },

      toggleCellStateCycle() {
        const next = !get().cellStateCycleEnabled;
        set({ cellStateCycleEnabled: next });
        return next;
      },

      toggleAutoPause() {
        const next = !get().autoPauseEnabled;
        set({ autoPauseEnabled: next });
        return next;
      },

      toggleSpanHints() {
        const next = !get().spanHintsEnabled;
        set({ spanHintsEnabled: next });
        return next;
      },

      toggleTimerVisible() {
        const next = !get().timerVisible;
        set({ timerVisible: next });
        return next;
      },

      saveTrainSettings(settings) {
        set({ trainSettings: normalizeTrainSettings(settings) });
      },
    }),
    {
      name: 'nonogram',
      merge: (persisted, current) => {
        const persistedState = persisted as Partial<GameState> | null;
        if (!persistedState) return current;

        return {
          ...current,
          ...persistedState,
          trainSettings: normalizeTrainSettings(persistedState.trainSettings),
        };
      },
      storage: {
        getItem: (name) => {
          try {
            const value = localStorage.getItem(name);
            return value ? JSON.parse(value) : null;
          } catch {
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch {
            // Quota exceeded or other storage error — silently ignore
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name);
          } catch {
            // Ignore
          }
        },
      },
      partialize: (s) => ({
        completions: s.completions,
        history: s.history,
        savedGames: s.savedGames,
        activeSaveByCategory: s.activeSaveByCategory,
        tutorialProgress: s.tutorialProgress,
        consent: s.consent,
        soundEnabled: s.soundEnabled,
        musicEnabled: s.musicEnabled,
        effectsEnabled: s.effectsEnabled,
        cellStateCycleEnabled: s.cellStateCycleEnabled,
        autoPauseEnabled: s.autoPauseEnabled,
        spanHintsEnabled: s.spanHintsEnabled,
        timerVisible: s.timerVisible,
        trainSettings: s.trainSettings,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        useGameStore.setState({
          trainSettings: normalizeTrainSettings(state.trainSettings),
        });

        if (
          Object.keys(state.savedGames ?? {}).length > 0 &&
          Object.keys(state.activeSaveByCategory ?? {}).length === 0
        ) {
          useGameStore.setState({
            activeSaveByCategory: deriveActiveSaves(state.savedGames ?? {}),
          });
        }
      },
    }
  )
);
